import { useState, useEffect } from 'preact/hooks'

const RESOURCE_CACHE = {}

type UseResourceArgs<T> = {
  serverSide: () => T | undefined
  clientSide: () => Promise<T>
  selector?: string
  cacheKey: string
}
/**
 * A hook that enables code-splitting of any DOM content.
 *
 * If the content has been server side rendered and useResource has been provided with a selector to find the content,
 * the content will be cached to prevent fetching resources that have been rendered in the DOM.
 */
export function useResource<T>(options: UseResourceArgs<T>): T | undefined {
  const {
    serverSide: serverSideRequireFn,
    clientSide: clientSideRequireFn,
    selector,
    cacheKey
  } = options
  const [resource, setResource] = useState<T | undefined>(() => {
    if (RESOURCE_CACHE[cacheKey] === undefined) {
      if (!__IS_SERVER__ && serverSideRequireFn()) {
        throw Error(
          `The server side require function for the resource '${cacheKey}' returned a value on the client side.\n` +
            'To prevent the resource accidentally being included in the client side bundle, the server side require function should always return falsy on the client side.\n' +
            'To do so use a guard like a `__IS_SERVER__` constant in webpack.\n'
        )
      }

      if (__IS_SERVER__ && typeof window === 'undefined') {
        RESOURCE_CACHE[cacheKey] = serverSideRequireFn()
      } else if (selector) {
        const element = document.querySelector(selector)
        if (element && element.outerHTML) {
          RESOURCE_CACHE[cacheKey] = element.outerHTML
          return RESOURCE_CACHE[cacheKey]
        }
      }
    }

    return RESOURCE_CACHE[cacheKey]
  })

  useEffect(() => {
    if (!resource) {
      clientSideRequireFn().then(setResource)
    }
  }, [])
  return resource
}
